import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../../Constant';
import { GlobalLoading, showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import { generateYearRange } from '../../../../../Custom/Custom';
import asiTitle from '../../../../../asi.json';
import BlockIData from '../../../../../assets/BlockIData.json';

// API configuration
const getAuthConfig = () => {
  let token = localStorage.getItem('jwt') ||
    localStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('userToken');

  if (!token) {
    console.error('No token found in localStorage or sessionStorage');
    return null;
  }

  return {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    }
  };
};

const BlockIReport = () => {
  const navigate = useNavigate();

  // State for report data
  const [itemData, setItemData] = useState({});
  const [blockIData, setBlockIData] = useState([]);
  const [blockFfieldData, setBlockFfieldData] = useState({ expenditure: 0 });
  const [blockHfieldData, setBlockHfieldData] = useState({ purchaseValue: 0 });
  const [fetchASIFormECallback, setFetchASIFormECallback] = useState({ formEItem14Value: 0 });
  const [loading, setLoading] = useState(true);

  // Memoized block info from localStorage
  const blockInfo = useMemo(() => {
    const balanceSheetData = localStorage.getItem('balanceSheetFormData');
    if (!balanceSheetData) return {};

    const parsedData = JSON.parse(balanceSheetData);
    return {
      dslNo: parsedData.dslNo || '',
      surveyYear: parsedData.year || '2022-23',
      nameUnit: parsedData.nameUnit || '',
      pslNo: parsedData.pslNo || ''
    };
  }, []);

  const storedLanguage = localStorage.getItem('language') || 'en';
  const itemCodeArray = ['9922100', '9994000'];

  // Memoized data transformation
  const transformBlockIData = useCallback((data) => {
    const initialData = {};
    data.forEach((item, index) => {
      initialData[index] = {
        id: index,
        dslNo: blockInfo.dslNo,
        surveyYear: blockInfo.surveyYear,
        itemCode: item.itemCode,
        itemDesc: item.itemDesc,
        purchaseValue: item.purchaseValue,
        qtyConsumed: item.qtyConsumed,
        ratePerUnit: item.ratePerUnit,
        unitOfQty: item.unitOfQty,
        statusFlag: item.statusFlag,
        itemCodeClass: 'textBox_back_color_white'
      };
    });
    return initialData;
  }, [blockInfo.dslNo, blockInfo.surveyYear]);

  // Function to add empty rows
  const addFiveEmptyRow = useCallback((responseBlockIData, dataType, emptyRowCount) => {
    let customObject = [];
    let mergedArray = '';
    for (let i = 0; i <= emptyRowCount; i++) {
      const newItemAdded = {
        "dslNo": blockInfo.dslNo,
        "surveyYear": blockInfo.surveyYear,
        "itemCode": '',
        "unitOfQty": '',
        "qtyConsumed": '',
        "purchaseValue": '',
        "ratePerUnit": '',
        "itemDesc": '',
        "statusFlag": '',
        "itemCodeClass": "textBox_back_color_white",
      };
      customObject.push(newItemAdded);
    }

    if (dataType === "dynamic") {
      const dataArray = Object.values(responseBlockIData);
      const AddedIndex = dataArray.length - 2
      mergedArray = [
        ...responseBlockIData.slice(0, AddedIndex),
        ...customObject,
        ...responseBlockIData.slice(AddedIndex)
      ];
    } else if (dataType === "static") {
      mergedArray = [
        ...responseBlockIData.slice(0, 0),
        ...customObject,
        ...responseBlockIData.slice(0)
      ];
    }

    return mergedArray;
  }, [blockInfo.dslNo, blockInfo.surveyYear]);

  // Fetch data function
  const fetchBlockIData = useCallback(async () => {
    if (!blockInfo.dslNo || !blockInfo.surveyYear) {
      console.log('Missing DSL No or Survey Year');
      return;
    }

    const requestPayload = {
      dsl_no: blockInfo.dslNo,
      survey_year: blockInfo.surveyYear.split('-')[0]
    };

    showLoading(true);

    try {
      const config = getAuthConfig();
      if (!config) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        return;
      }

      const response = await axios.post(`${API_URL}api/asi/blockIForm`, requestPayload, config);
      const { data, status } = response.data;

      let responseBlockIData = '';

      if (status && data?.blockIData?.length > 0) {
        responseBlockIData = addFiveEmptyRow(data.blockIData, 'dynamic', 4);
        setBlockFfieldData(data.blockFfieldData || { expenditure: 0 });
        setBlockHfieldData(data.blockHfieldData || { purchaseValue: 0 });
        setFetchASIFormECallback(data.fetchASIFormECallback || { formEItem14Value: 0 });
      } else {
        responseBlockIData = addFiveEmptyRow(BlockIData, 'static', 4);
      }

      setBlockIData(responseBlockIData);
      const transformedData = transformBlockIData(responseBlockIData);
      setItemData(transformedData);
      console.log('Block I data loaded successfully:', responseBlockIData.length, 'items');
    } catch (error) {
      console.error('API Error:', error);

      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.warning('No data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      } else {
        toast.error(`Error loading data: ${error.message}`, messages.COLOR_RED);
      }
      setBlockIData([]);
    } finally {
      showLoading(false);
    }
  }, [blockInfo.dslNo, blockInfo.surveyYear, transformBlockIData, addFiveEmptyRow]);

  useEffect(() => {
    fetchBlockIData();
  }, [fetchBlockIData]);

  // Memoized table headers
  const tableHeaders = useMemo(() => {
    const isHindi = storedLanguage === 'hi';
    return {
      slNo: isHindi ? asiTitle.hi.SL_NO : asiTitle.en.SL_NO,
      itemDescription: isHindi ? asiTitle.hi.ITEM_DESCRIPTION_BLOCK_I : asiTitle.en.ITEM_DESCRIPTION_BLOCK_I,
      itemCode: isHindi ? asiTitle.hi.ITEM_CODE : asiTitle.en.ITEM_CODE,
      unitOfQuantity: isHindi ? asiTitle.hi.UNIT_OF_QUANTITY : asiTitle.en.UNIT_OF_QUANTITY,
      quantityConsumed: isHindi ? asiTitle.hi.QUANTITY_CONSUMED : asiTitle.en.QUANTITY_CONSUMED,
      purchaseValue: isHindi ? asiTitle.hi.PURCHASE_VALUE : asiTitle.en.PURCHASE_VALUE,
      ratePerUnit: isHindi ? asiTitle.hi.RATE_PER_UNIT : asiTitle.en.RATE_PER_UNIT,
      dslNo: isHindi ? asiTitle.hi.DSL_NO : asiTitle.en.DSL_NO,
      surveyYear: isHindi ? asiTitle.hi.SERVEY_YEAR : asiTitle.en.SERVEY_YEAR,
      blockITitle: isHindi ? asiTitle.hi.BLOCK_I_TITLE : asiTitle.en.BLOCK_I_TITLE
    };
  }, [storedLanguage]);

  return (
    <>
      <GlobalLoading />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

      <style>
        {`
          .report-container {
            background-color: #fff;
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 20px;
            margin-bottom: 20px;
          }
          .report-title {
            background-color: #fff;
            color: #333;
            font-size: 18px;
            font-weight: bold;
            padding: 15px 20px;
            margin: -20px -20px 20px -20px;
            border-radius: 6px 6px 0 0;
            border-bottom: 2px solid #333;
            text-align: center;
          }
          .report-info {
            background-color: #f8f9fa;
            padding: 10px 15px;
            margin-bottom: 20px;
            border-radius: 4px;
            border-left: 4px solid #333;
          }
          .report-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            font-size: 11px;
          }
          .report-table th,
          .report-table td {
            border: 1px solid #333;
            padding: 4px 2px;
            text-align: center;
            vertical-align: middle;
          }
          .report-table th {
            background-color: #f5f5f5;
            color: #333;
            font-weight: bold;
            font-size: 10px;
          }
          .report-table td {
            background-color: #fff;
          }
          .report-table .item-name {
            text-align: left;
            font-weight: 500;
            padding-left: 4px;
          }
          .report-table .bold-text {
            font-weight: bold;
          }
          .no-data {
            text-align: center;
            color: #666;
            font-style: italic;
            padding: 40px;
            background-color: #f8f9fa;
            border-radius: 4px;
          }
         
          @media print {
            .no-print { display: none !important; }
            .report-container { border: none; margin: 0; padding: 0; }
            .report-title { margin: 0; border-radius: 0; }
            .report-info { margin: 0 0 20px 0; }
            .btn_orange { display: none !important; }
          }
        `}
      </style>

      <div className="container-fluid py-3">
        {/* Report Content */}
        <div className="report-container">
          <div className="report-title">
            <div dangerouslySetInnerHTML={{ __html: tableHeaders.blockITitle }} />
          </div>

          <div className="report-info">
            <div className="row">
              <div className="col-md-6">
                <strong>{tableHeaders.dslNo}:</strong> {blockInfo.dslNo || 'Not Available'}
              </div>
              <div className="col-md-6">
                <strong>{tableHeaders.surveyYear}:</strong> {blockInfo.surveyYear || 'Not Available'}
              </div>
            </div>
          </div>

          {blockIData.length > 0 ? (
            <div className="table-responsive">
              <table className="report-table">
                <thead>
                  <tr>
                    <th style={{ width: '8%' }}>{tableHeaders.slNo}</th>
                    <th style={{ width: '25%' }}>{tableHeaders.itemDescription}</th>
                    <th style={{ width: '12%' }}>{tableHeaders.itemCode}</th>
                    <th style={{ width: '12%' }}>{tableHeaders.unitOfQuantity}</th>
                    <th style={{ width: '15%' }}>{tableHeaders.quantityConsumed}</th>
                    <th style={{ width: '15%' }}>{tableHeaders.purchaseValue}</th>
                    <th style={{ width: '13%' }}>{tableHeaders.ratePerUnit}</th>
                  </tr>
                  <tr>
                    <th>1</th>
                    <th>2</th>
                    <th>3</th>
                    <th>4</th>
                    <th>5</th>
                    <th>6</th>
                    <th>7</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(itemData).map((item, i) => {
                    let srNo = i + 1; // Start from 1, not 0
                    let isDisable = false;

                    if (itemCodeArray.includes(item.itemCode) && i >= Object.values(itemData).length - 2) {
                      isDisable = true;
                    }

                    const isBold = ['9994000'].includes(item.itemCode);

                    return (
                      <tr key={i}>
                        <td>{srNo}</td>
                        <td className={`item-name ${isBold ? "bold-text" : ""}`}>
                          {item.itemDesc}
                        </td>
                        <td>{item.itemCode}</td>
                        <td>{item.unitOfQty}</td>
                        <td>{item.qtyConsumed === "0" ? "" : item.qtyConsumed || ''}</td>
                        <td>{item.purchaseValue === "0" ? "" : item.purchaseValue || ''}</td>
                        <td>{item.ratePerUnit === "0" ? "" : item.ratePerUnit || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">No Block I data available for this DSL No.</div>
          )}
        </div>

        {/* Action Buttons */}

        {/* <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print  
              <i className="fa fa-print ms-1"></i>
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/reports')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div> */}

        <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/report-selection')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/blockJreport')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>

      </div>
    </>
  );
};

export default BlockIReport;